import { z } from "zod";
import { buildWhereFromState } from "#app/lib/filters/build-where";
import type { FilterState } from "#app/lib/filters/types";
import { combineWhereConditions } from "#app/lib/query-utils";
import { datePresetValueSchema, type DatePresetValue } from "#app/lib/utils/date-presets";

/**
 * A widget's own persisted filter blob — the config the settings drawer's
 * "Filters" section edits (one `filters` key, committed as a whole). Every part
 * narrows the widget's data window on top of the ambient dashboard scope:
 * `dateRange` overrides the toolbar window (the `useScopedWhere` override seam),
 * `showArchived` lifts the default archived-out predicate, and `query` is a
 * saved advanced-filter builder state compiled to a `where` at read time.
 * Widgets opt in by declaring a `kind: "issueFilters"` settings field and
 * folding {@link resolveWidgetFilters} into their data hook; the Data drill-down
 * folds the same blob so its rows stay the widget's exact inputs.
 */

/** Structural mirror of {@link FilterState} — `operator` is validated as a plain
 * string (the builder UI only ever writes registered operators). */
const filterStateSchema = z.object({
  logic: z.enum(["AND", "OR"]),
  blocks: z.array(
    z.object({
      logic: z.enum(["AND", "OR"]),
      filters: z.array(
        z.object({ field: z.string(), operator: z.string(), value: z.unknown() }),
      ),
    }),
  ),
});

export const widgetFiltersSchema = z
  .looseObject({
    dateRange: datePresetValueSchema.nullish(),
    showArchived: z.boolean().optional(),
    query: filterStateSchema.nullish(),
  });

export type WidgetFilters = z.infer<typeof widgetFiltersSchema>;

const NO_FILTERS: WidgetFilters = {};

/** Parse an unknown config value into a filter blob (invalid/absent → empty). */
export function parseWidgetFilters(value: unknown): WidgetFilters {
  const parsed = widgetFiltersSchema.safeParse(value ?? {});
  return parsed.success ? parsed.data : NO_FILTERS;
}

/**
 * Fold a widget's filter blob into `useScopedWhere` inputs: `base` is the
 * widget's own predicate (archived-out unless `showArchived`, AND the compiled
 * `query`), `override` carries the widget-owned date window when one is set.
 *
 * ```ts
 * const { base, override } = resolveWidgetFilters(config.filters);
 * const { where } = useScopedWhere(base, { override });
 * ```
 */
export function resolveWidgetFilters(value: unknown): {
  filters: WidgetFilters;
  base: Record<string, unknown>;
  override: { dateRange: DatePresetValue } | undefined;
} {
  const filters = parseWidgetFilters(value);
  const conditions: Record<string, unknown>[] = [];
  if (!filters.showArchived) conditions.push({ archived: { eq: false } });
  const queryWhere = filters.query
    ? buildWhereFromState(filters.query as FilterState)
    : undefined;
  if (queryWhere) conditions.push(queryWhere);
  return {
    filters,
    base: combineWhereConditions(conditions) ?? {},
    override: filters.dateRange ? { dateRange: filters.dateRange } : undefined,
  };
}
